/* Moments Etched v15.5.0 — Studio-aware accessibility behavior */
(() => {
  'use strict';

  const announce = (message) => {
    const node = document.getElementById('me-a11y-announcer');
    if (!node || !message) return;
    node.textContent = '';
    window.setTimeout(() => { node.textContent = message; }, 30);
  };

  const isVisible = (element) => Boolean(element && !element.closest('[hidden]') && element.getClientRects().length);
  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  function groupForItem(item) {
    if (!item) return null;
    if (item.matches('[data-me-designer-color]')) {
      const container = item.closest('[data-me-designer-color-swatches]');
      return container ? { container, selector: '[data-me-designer-color]', selectOnArrow: false } : null;
    }
    if (item.matches('[data-me-order-mode]')) {
      const container = item.closest('.me-order-type__options');
      return container ? { container, selector: '[data-me-order-mode]', selectOnArrow: true } : null;
    }
    if (item.matches('[data-me-size-button]')) {
      const container = item.closest('[data-me-size-grid]');
      return container ? { container, selector: '[data-me-size-button]', selectOnArrow: false } : null;
    }
    if (item.matches('[data-me-placement-checkbox]')) {
      const container = item.closest('.me-print-location-grid');
      return container ? { container, selector: '[data-me-placement-checkbox]', selectOnArrow: item.type === 'radio' } : null;
    }
    if (item.matches('[data-me-navigator-placement]')) {
      const container = item.closest('.me-designer-location-nav');
      return container ? { container, selector: '[data-me-navigator-placement]', selectOnArrow: false } : null;
    }
    return null;
  }

  function itemsFor(group) {
    if (!group?.container) return [];
    return Array.from(group.container.querySelectorAll(group.selector)).filter((item) => !item.disabled && isVisible(item));
  }

  function preferredItem(items) {
    return items.find((item) =>
      item.checked ||
      item.getAttribute('aria-checked') === 'true' ||
      item.getAttribute('aria-pressed') === 'true' ||
      item.classList.contains('is-active')
    ) || items.find((item) => item.tabIndex === 0) || items[0];
  }

  function setRoving(group, preferred = null) {
    const items = itemsFor(group);
    if (!items.length) return;
    const target = preferred && items.includes(preferred) ? preferred : preferredItem(items);
    items.forEach((item) => item.setAttribute('tabindex', item === target ? '0' : '-1'));
  }

  function refreshRoving(designer) {
    const containers = [
      ...designer.querySelectorAll('[data-me-designer-color-swatches]'),
      ...designer.querySelectorAll('.me-order-type__options'),
      ...designer.querySelectorAll('[data-me-size-grid]'),
      ...designer.querySelectorAll('.me-print-location-grid'),
      ...designer.querySelectorAll('.me-designer-location-nav')
    ];

    containers.forEach((container) => {
      let selector = null;
      if (container.matches('[data-me-designer-color-swatches]')) selector = '[data-me-designer-color]';
      else if (container.matches('.me-order-type__options')) selector = '[data-me-order-mode]';
      else if (container.matches('[data-me-size-grid]')) selector = '[data-me-size-button]';
      else if (container.matches('.me-print-location-grid')) selector = '[data-me-placement-checkbox]';
      else if (container.matches('.me-designer-location-nav')) selector = '[data-me-navigator-placement]';
      if (selector) setRoving({ container, selector });
    });
  }

  function syncHeaderNames(designer) {
    const quote = designer.querySelector('[data-me-quote-header]');
    if (quote && !quote.getAttribute('aria-label')) quote.setAttribute('aria-label', 'Request a bulk quote for 21 or more shirts');
    const save = designer.querySelector('[data-me-save-design]');
    if (save && !save.getAttribute('aria-label')) save.setAttribute('aria-label', 'Save design');
  }

  function syncPlacementGuide(designer) {
    const guide = designer.querySelector('.me-studio-placement-guide');
    const heading = guide?.querySelector('h3');
    if (!guide || !heading) return;
    if (!heading.id) heading.id = 'me-placement-guide-title';
    guide.setAttribute('aria-labelledby', heading.id);
  }

  function syncPreviewButtons(designer) {
    const container = designer.querySelector('.me-studio-preview-tabs');
    if (!container) return;
    const current = String(designer.querySelector('[data-me-preview-placement-label]')?.textContent || '').trim();
    container.querySelectorAll('[data-me-preview-placement]').forEach((button) => {
      const active = String(button.textContent || '').trim() === current;
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (!button.getAttribute('aria-label')) button.setAttribute('aria-label', `Preview ${String(button.textContent || '').trim()} placement`);
    });
  }

  function syncStudioProgress(designer, announceChange = false) {
    const progress = designer.querySelector('.me-studio-progress');
    if (!progress) return;
    const stage = Math.max(1, Math.min(5, Number(designer.dataset.meStudioStage || 1) || 1));
    const buttons = Array.from(progress.querySelectorAll('[data-me-studio-jump]'));
    progress.setAttribute('aria-label', `Personalization progress, step ${stage} of 5`);

    buttons.forEach((button, index) => {
      const n = index + 1;
      const label = String(button.querySelector('.me-studio-progress__label')?.textContent || `Step ${n}`).trim();
      const state = n === stage ? ', current step' : n < stage ? ', completed; activate to return' : ', not yet available';
      button.setAttribute('aria-label', `Step ${n} of 5, ${label}${state}`);
      button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
    });

    const status = designer.querySelector('[data-me-studio-status]');
    if (status) {
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
    }

    const current = progress.querySelector('[aria-current="step"]');
    if (current && window.matchMedia?.('(max-width: 749px)').matches) {
      const targetLeft = Math.max(0, current.offsetLeft - (progress.clientWidth - current.offsetWidth) / 2);
      progress.scrollTo?.({ left: targetLeft, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    if (announceChange) {
      const label = String(current?.querySelector('.me-studio-progress__label')?.textContent || '').trim();
      if (label) announce(`Step ${stage} of 5: ${label}.`);
    }
  }

  function initDesigner(root = document) {
    const designer = root.matches?.('[data-me-designer]') ? root : root.querySelector?.('[data-me-designer]');
    if (!designer || designer.dataset.meA11y1550Ready === 'true') return;
    designer.dataset.meA11y1550Ready = 'true';

    syncHeaderNames(designer);
    syncPlacementGuide(designer);
    refreshRoving(designer);

    const legacyProgress = designer.querySelector('.me-builder-progress__bar[role="progressbar"]');
    const legacyButtons = Array.from(designer.querySelectorAll('[data-me-step-button]'));
    const legacyActiveStep = () => designer.dataset.meCurrentStep || legacyButtons.find((b) => b.getAttribute('aria-current') === 'step')?.dataset.meStepButton || 'design';

    const updateLegacyProgress = () => {
      if (designer.classList.contains('me-studio-v152')) return;
      const step = legacyActiveStep();
      const index = Math.max(0, ['design', 'artwork', 'review'].indexOf(step));
      legacyProgress?.setAttribute('aria-valuenow', String(index + 1));
      legacyProgress?.setAttribute('aria-valuetext', `Step ${index + 1} of 3`);
    };

    const focusLegacyCurrentStep = () => {
      if (designer.classList.contains('me-studio-v152')) return;
      const step = legacyActiveStep();
      const heading = designer.querySelector(`[data-me-step-focus="${step}"]`);
      if (!heading || heading.closest('[hidden]')) return;
      heading.focus({ preventScroll: true });
      heading.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
      announce(`${heading.textContent.trim()}. Step ${['design','artwork','review'].indexOf(step) + 1} of 3.`);
    };

    designer.addEventListener('keydown', (event) => {
      const item = event.target.closest?.('[data-me-designer-color], [data-me-order-mode], [data-me-size-button], [data-me-placement-checkbox], [data-me-navigator-placement]');
      if (!item) return;
      const group = groupForItem(item);
      if (!group) return;
      const items = itemsFor(group);
      if (items.length < 2) return;

      const key = event.key;
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) return;
      event.preventDefault();

      const current = Math.max(0, items.indexOf(item));
      let next = current;
      if (key === 'Home') next = 0;
      else if (key === 'End') next = items.length - 1;
      else if (key === 'ArrowRight' || key === 'ArrowDown') next = (current + 1) % items.length;
      else next = (current - 1 + items.length) % items.length;

      const target = items[next];
      setRoving(group, target);
      target.focus();
      if (group.selectOnArrow) target.click();
    });

    designer.addEventListener('focusin', (event) => {
      const item = event.target.closest?.('[data-me-designer-color], [data-me-order-mode], [data-me-size-button], [data-me-placement-checkbox], [data-me-navigator-placement]');
      const group = groupForItem(item);
      if (group) setRoving(group, item);
    });

    designer.addEventListener('click', (event) => {
      const groupedItem = event.target.closest?.('[data-me-designer-color], [data-me-order-mode], [data-me-size-button], [data-me-placement-checkbox], [data-me-navigator-placement]');
      const group = groupForItem(groupedItem);
      if (group) window.setTimeout(() => setRoving(group, groupedItem), 0);

      if (event.target.closest?.('[data-me-preview-placement]')) {
        window.setTimeout(() => syncPreviewButtons(designer), 0);
      }

      /* The five-stage Studio owns its own focus movement. Do not let the
         hidden legacy three-step engine pull focus back to invisible headings. */
      if (designer.classList.contains('me-studio-v152')) return;

      const stepButton = event.target.closest?.('[data-me-step-button]');
      const primary = event.target.closest?.('[data-me-designer-primary-action]');
      if (!stepButton && !primary) return;
      const before = legacyActiveStep();
      window.setTimeout(() => {
        updateLegacyProgress();
        refreshRoving(designer);
        if (legacyActiveStep() !== before) focusLegacyCurrentStep();
      }, 0);
    });

    designer.addEventListener('change', (event) => {
      const placement = event.target.closest?.('[data-me-placement-checkbox]');
      if (!placement) return;
      const selected = Array.from(designer.querySelectorAll('[data-me-placement-checkbox]:checked')).map((input) => input.value);
      announce(`${placement.value} ${placement.checked ? 'selected' : 'not selected'}. Current print locations: ${selected.join(', ') || 'none'}. Placement is automatic.`);
      const group = groupForItem(placement);
      if (group) setRoving(group, placement);
    });

    const proof = designer.querySelector('[data-me-proof-checkbox]');
    proof?.addEventListener('change', () => {
      announce(proof.checked
        ? 'Digital proof is required. Production will wait for written approval.'
        : 'Digital proof requirement changed.');
    });

    const cleanGeneratedCards = () => {
      designer.querySelectorAll('.me-location-artwork[tabindex="0"]').forEach((card) => card.removeAttribute('tabindex'));
    };

    let previousStudioStage = Number(designer.dataset.meStudioStage || 0);
    const observer = new MutationObserver((mutations) => {
      cleanGeneratedCards();
      refreshRoving(designer);
      syncHeaderNames(designer);
      syncPlacementGuide(designer);
      syncPreviewButtons(designer);

      const stage = Number(designer.dataset.meStudioStage || 0);
      const changed = stage > 0 && stage !== previousStudioStage;
      if (stage > 0) previousStudioStage = stage;
      syncStudioProgress(designer, changed && designer.classList.contains('is-open'));
    });
    observer.observe(designer, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-me-studio-stage', 'disabled'] });

    const previewLabel = designer.querySelector('[data-me-preview-placement-label]');
    if (previewLabel) {
      new MutationObserver(() => syncPreviewButtons(designer)).observe(previewLabel, { childList: true, characterData: true, subtree: true });
    }

    cleanGeneratedCards();
    updateLegacyProgress();
    window.setTimeout(() => {
      syncStudioProgress(designer, false);
      syncPreviewButtons(designer);
    }, 0);
  }

  initDesigner();
  document.addEventListener('shopify:section:load', (event) => initDesigner(event.target));
})();
